/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Talking to the API, and the staff session.  CA-31, CA-17.
 *
 * Ported from admin-live.js with every rule it had learned intact:
 *  - EVERY request says `Accept-Language: no` (fault 111). The panel is where
 *    the Norwegian master is written; a browser set to English must not be
 *    served, and then save back, the machine English of the mosque's words.
 *  - `send()` makes ONE request and returns a 400 as an answer (field errors
 *    in Norwegian), never as an exception. The first version POSTed twice.
 *  - A 401 ends the session and brings back the sign-in form, and
 *    `session_expired` is said differently from `unauthorized`.
 *  - The session lives in sessionStorage, not localStorage: it is the power
 *    to announce a death to every phone, on a shared office computer.
 *    Closing the tab ends it.
 */
export type Body = Record<string, any>;
export interface Session {
  token: string;
  user: { id: string; name: string; email?: string; role: string };
  permissions: string[];
  expiresAt: string;
}
export interface Sent { ok: boolean; status: number; body: Body }

const EDIT_LANG = 'no';
/**
 * ?api= for a demo, a test, or a mosque on the Min Masjid platform — the first
 * administrator's link carries its mosque's API (ADR-004). The address bar
 * keeps it (hash routing leaves the query alone), so a bookmark opens the
 * right mosque; it is also kept for the tab, in sessionStorage like the
 * session — never localStorage (panel test 4). Then VITE_MASJID_API at build
 * time, else the API on this origin.
 */
const API_KEY = 'masjid.admin.api';
const LOCAL = /^(localhost|127\.0\.0\.1|\[::1\])$/;

/**
 * Whether `?api=` may be believed.  2026-09-28.
 *
 * It used to take ANY address: a link to the real panel on the real domain
 * with ?api=https://evil.example/v1 sent the staff member's e-mail and
 * password (or an invite token and a new password) to that server. Now the
 * address must be the API's own path — /v1, or /t/<slug>/v1 on a platform
 * without a domain — on one of:
 *  - this origin;
 *  - the platform's API host beside this panel, over https: api.<parent>
 *    (cjas.masjid.no → api.masjid.no) — that name exactly;
 *  - localhost, when the panel itself is on localhost (development);
 *  - a host named at build time in VITE_MASJID_API_HOSTS (comma-separated).
 * Anything else is ignored, and the panel talks to its own origin.
 *
 * P-03 / S-09 (audit 2026-10-19): the second rule used to be "any name under
 * the parent domain". That took the bare parent — masjid.no, which is not the
 * platform but a website hosted elsewhere — and every sibling, so a link to
 * the real panel could post a staff password to whatever answers there. It
 * also took *.staging.masjid.no from the production panel: production
 * credentials typed into the staging API. A staging host is now refused from
 * a panel that is not itself on staging, even when named at build time.
 */
export function trustedApi(raw: string, here: { origin: string }, allowHosts: string[] = []): string | null {
  let u: URL;
  try { u = new URL(raw, here.origin); } catch { return null; }
  const me = new URL(here.origin);
  if (u.username || u.password || u.search || u.hash) return null;
  if (!/^\/(t\/[a-z0-9-]{2,32}\/)?v1\/?$/.test(u.pathname)) return null;
  const abs = u.toString().replace(/\/$/, '');
  if (u.origin === me.origin) return abs;
  if (LOCAL.test(u.hostname) && LOCAL.test(me.hostname) && u.protocol === 'http:') return abs;
  if (u.protocol !== 'https:') return null;
  const staging = (host: string) => host.split('.').includes('staging');
  if (staging(u.hostname) && !staging(me.hostname)) return null;
  if (allowHosts.includes(u.hostname)) return abs;
  const labels = me.hostname.split('.');
  if (labels.length >= 3 && u.hostname === 'api.' + labels.slice(1).join('.')) return abs;
  return null;
}

function resolveApi(): string {
  const allow = String((import.meta as any).env?.VITE_MASJID_API_HOSTS || '').split(',').map((h) => h.trim()).filter(Boolean);
  const q = new URLSearchParams(location.search).get('api');
  const fromLink = q ? trustedApi(q, location, allow) : null;
  if (fromLink) {
    try { sessionStorage.setItem(API_KEY, fromLink); } catch { /* storage blocked: it still works for this visit */ }
    return fromLink;
  }
  const env = (import.meta as any).env?.VITE_MASJID_API as string | undefined;
  if (env) return env.replace(/\/$/, '');
  try {
    const saved = sessionStorage.getItem(API_KEY);
    const ok = saved ? trustedApi(saved, location, allow) : null;
    if (ok) return ok;
  } catch { /* storage blocked */ }
  return location.origin + '/v1';
}
export const API = resolveApi();
/**
 * The session, per API: a session from one mosque's panel is never sent to
 * another's. The one-mosque install keeps the key it always had.
 */
const KEY = API === location.origin + '/v1' ? 'masjid.admin.session' : 'masjid.admin.session:' + API;
/** The API's origin, for the TV address and file downloads. */
export const API_ORIGIN = API.replace(/\/v1\/?$/, '');

/* ---------------- the session, as a tiny external store ---------------- */

type AuthEvent = { kind: 'expired' | 'signed-out' | 'signed-in' };
let session: Session | null = load();
let offline = false;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((f) => f());
export const subscribe = (f: () => void) => { listeners.add(f); return () => { listeners.delete(f); }; };
export const getSession = () => session;
export const isOffline = () => offline;
let lastAuthEvent: AuthEvent | null = null;
export const getAuthEvent = () => lastAuthEvent;

function load(): Session | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as Session;
    // A session past its expiry is not a session: show the form, not a
    // screenful of refused requests.
    if (!s.expiresAt || new Date(s.expiresAt) <= new Date()) return null;
    return s;
  } catch { return null; }
}
export function setSession(s: Session | null, why: AuthEvent['kind'] = s ? 'signed-in' : 'signed-out') {
  session = s;
  lastAuthEvent = { kind: why };
  try { if (s) sessionStorage.setItem(KEY, JSON.stringify(s)); else sessionStorage.removeItem(KEY); } catch { /* private mode */ }
  emit();
}
function setOffline(v: boolean) { if (offline !== v) { offline = v; emit(); } }

export function can(permission: string): boolean {
  return Boolean(session && session.permissions && session.permissions.includes(permission));
}

/* ---------------- requests ---------------- */

export class ApiError extends Error {
  status: number; body: Body;
  constructor(message: string, status: number, body: Body) { super(message); this.status = status; this.body = body; }
}

function headers(extra?: HeadersInit): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    'Accept-Language': EDIT_LANG,
    ...(session ? { Authorization: 'Bearer ' + session.token } : {}),
    ...(extra as Record<string, string> || {}),
  };
}

async function raw(path: string, opts: RequestInit = {}): Promise<Sent> {
  let r: Response;
  try {
    r = await fetch(API + '/admin' + path, { ...opts, headers: headers(opts.headers) });
  } catch {
    setOffline(true);
    throw new ApiError('Får ikke kontakt med serveren.', 0, {});
  }
  setOffline(false);
  let body = await r.json().catch(() => ({}));
  if (!body || typeof body !== 'object' || Array.isArray(body)) body = {};
  // A refusal with no words of its own (a proxy's 502 page, an empty 500) gets a sentence, never «HTTP 502».
  if (!r.ok && !body.message_no && !body.message && !body.errors) body.message_no = statusText(r.status);
  // The API's catch-all 404 is in English («Unknown path.»): a panel older than the API (or the reverse) showed that.
  if (!r.ok && !body.message_no && /^Unknown (admin )?path\.$/.test(String(body.message || ''))) body.message = body.message_no = statusText(r.status);
  if (r.status === 401 && session) {
    // `session_expired` and `unauthorized` are different problems and send a
    // person looking in different places.
    setSession(null, body.code === 'session_expired' ? 'expired' : 'signed-out');
  }
  return { ok: r.ok, status: r.status, body };
}

/**
 * A failure without the server's own words, said so a volunteer knows what to do (QA C, 2026-10-16: the panel showed
 * «HTTP 502»). The status stays in the sentence, in brackets, for whoever they ring.
 */
export function statusText(status: number): string {
  if (status === 401) return 'Du er logget ut. Logg inn igjen.';
  if (status === 403) return 'Du har ikke tilgang til dette. Be en administrator om tilgang.';
  if (status === 404) return 'Fant det ikke. Det kan være slettet, eller adressen er feil.';
  if (status === 409) return 'Noen andre har endret dette i mellomtiden. Last inn siden på nytt og prøv igjen.';
  if (status === 413) return 'Det du sendte er for stort.';
  if (status === 429) return 'For mange forsøk på kort tid. Vent litt og prøv igjen.';
  if (status === 502 || status === 503 || status === 504) return `Serveren svarer ikke akkurat nå. Prøv igjen om et par minutter (feil ${status}).`;
  if (status >= 500) return `Noe gikk galt på serveren. Prøv igjen; skjer det igjen, si fra til Masjid (feil ${status}).`;
  return `Det gikk ikke (feil ${status}).`;
}

/** For reads: throws on anything but 2xx. */
export async function call<T = Body>(path: string, opts: RequestInit = {}): Promise<T> {
  const r = await raw(path, opts);
  if (!r.ok) throw new ApiError(r.body.message_no || r.body.message || statusText(r.status), r.status, r.body);
  return r.body as T;
}

/** For writes and forms: a refusal is an answer, with its reasons in it. */
export async function send(path: string, opts: RequestInit = {}): Promise<Sent> {
  try { return await raw(path, opts); }
  catch (e) { return { ok: false, status: 0, body: { message_no: (e as Error).message } }; }
}

export const post = (path: string, body: unknown = {}) => send(path, { method: 'POST', body: JSON.stringify(body) });
/** Editing something that already exists — a partner's offer (CA-128), say. */
export const patch = (path: string, body: unknown = {}) => send(path, { method: 'PATCH', body: JSON.stringify(body) });

/**
 * A refusal in words a person can act on. `errors` is a map from some routes
 * and a list from others — the list was once shown as "[object Object]".
 */
// A field may carry a list of sentences ({ phone: ['…', '…'] }): said one after the other, not dropped.
const errOne = (e: unknown): string => typeof e === 'string' ? e : Array.isArray(e) ? e.map(errOne).filter(Boolean).join(' ')
  : (e && ((e as Body).message_no || (e as Body).message)) || '';
/** The field errors as { field: sentence }, for showing each under its field (a list is keyed by its `field`). */
export function fieldErrors(b: Body | null | undefined): Record<string, string> {
  const errs = (b && b.errors) || {};
  const out: Record<string, string> = {};
  if (Array.isArray(errs)) { for (const e of errs) if (e && e.field && errOne(e)) out[e.field] = errOne(e); }
  else for (const [k, v] of Object.entries(errs)) { const t = errOne(v); if (t) out[k] = t; }
  return out;
}
/**
 * The line by «Lagre» when the fields themselves say what is wrong (polish pass 2026-10-30). With one field it is that
 * field's sentence; with several it was only the FIRST one, so a form with three mistakes said one and looked done.
 */
export function errSummary(b: Body | null | undefined): string {
  const n = Object.keys(fieldErrors(b)).length;
  return n > 1 ? `${n} felt må rettes. Se merknadene ved feltene.` : errText(b);
}
export function errText(b: Body | null | undefined, labels?: Record<string, string>): string {
  const errs = (b && b.errors) || {};
  // With `labels`, a field error names its field: "Tidspunkt: Må fylles ut."
  // rather than "Må fylles ut." twice with no way to tell which box
  // (found porting the janazah composer, where two fields said exactly that).
  const parts = Array.isArray(errs) ? errs.map(errOne)
    : Object.entries(errs).map(([k, v]) => { const t = errOne(v); return t && labels && labels[k] ? `${labels[k]}: ${t}` : t; });
  return parts.filter(Boolean).join(' ') || (b && (b.message_no || b.message)) || 'Det gikk ikke.';
}
/** The fields a refusal names, for aria-invalid. */
export const errFields = (b: Body | null | undefined): string[] => {
  const errs = (b && b.errors) || {};
  return Array.isArray(errs) ? errs.map((e: Body) => e && e.field).filter(Boolean) : Object.keys(errs);
};

/* ---------------- sign-in, outside /admin ---------------- */

export async function publicPost(path: string, body: unknown): Promise<Sent> {
  try {
    const r = await fetch(API + path, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    setOffline(false);
    return { ok: r.ok, status: r.status, body: await r.json().catch(() => ({})) };
  } catch {
    setOffline(true);
    return { ok: false, status: 0, body: { message_no: 'Får ikke kontakt med serveren.' } };
  }
}

export async function health(): Promise<{ now?: string } | null> {
  try { const r = await fetch(API + '/health', { cache: 'no-store' }); setOffline(!r.ok); return r.ok ? await r.json() : null; }
  catch { setOffline(true); return null; }
}

/**
 * An authenticated download (request documents, CA-27): fetched with the
 * session and handed to the browser as a blob. Never a bare link — a link
 * carries no token, and a token in a URL is in the history.
 */
export async function download(path: string, filename: string): Promise<{ ok: boolean; status: number; message?: string }> {
  try {
    const r = await fetch(API_ORIGIN + path, { headers: session ? { Authorization: 'Bearer ' + session.token } : {} });
    if (r.status === 401) { setSession(null, 'expired'); return { ok: false, status: 401 }; }
    if (!r.ok) { const b = await r.json().catch(() => ({})); return { ok: false, status: r.status, message: (b && (b.message_no || b.message)) || statusText(r.status) }; }
    saveBlob(await r.blob(), filename);
    return { ok: true, status: 200 };
  } catch { return { ok: false, status: 0 }; }
}

/**
 * A picture behind the session — a draft product's (store iteration 2) — as an object URL for an <img>. Fetched with
 * the session, like download(): an <img src> carries no token, and a draft's picture is not public. Null when it
 * cannot be had (the caller shows its placeholder). The caller revokes the URL when it is done with it.
 */
export async function objectUrl(path: string): Promise<string | null> {
  try {
    const r = await fetch(API + '/admin' + path, { headers: session ? { Authorization: 'Bearer ' + session.token } : {} });
    if (r.status === 401 && session) { setSession(null, 'expired'); return null; }
    if (!r.ok || !/^image\/(jpeg|png|webp)$/.test(r.headers.get('content-type') || '')) return null;
    return URL.createObjectURL(await r.blob());
  } catch { return null; }
}

/**
 * An authenticated picture (banner access patch, 2026-10-04): fetched with the session, like download(), and handed
 * back as an object URL for an <img>. An <img src> carries no token. The caller revokes the URL when done.
 * null when it cannot be had (signed out, no permission, gone).
 */
export async function blobUrl(path: string): Promise<string | null> {
  try {
    const r = await fetch(API + path, { headers: session ? { Authorization: 'Bearer ' + session.token } : {}, cache: 'no-store' });
    if (r.status === 401) { setSession(null, 'expired'); return null; }
    if (!r.ok) return null;
    return URL.createObjectURL(await r.blob());
  } catch { return null; }
}

export function saveBlob(blob: Blob, filename: string) {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob); a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 60000);
}

/**
 * A file for an upload endpoint: an .xlsx as it is (base64 — Excel must not
 * get the chance to round a number or reformat a time), anything else as text.
 */
export function readUpload(file: File): Promise<Body> {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onerror = () => reject(new Error('Filen kunne ikke leses.'));
    if (/\.xlsx$/i.test(file.name)) {
      fr.onload = () => {
        const b = new Uint8Array(fr.result as ArrayBuffer); let bin = '';
        for (let i = 0; i < b.length; i += 0x8000) bin += String.fromCharCode.apply(null, Array.from(b.subarray(i, i + 0x8000)));
        resolve({ xlsxBase64: btoa(bin), filename: file.name });
      };
      fr.readAsArrayBuffer(file);
    } else { fr.onload = () => resolve({ csv: String(fr.result), filename: file.name }); fr.readAsText(file); }
  });
}

export async function copyText(text: string): Promise<boolean> {
  try { await navigator.clipboard.writeText(text); return true; } catch { return false; }
}

/** A scanned form for the register (PDF, JPEG or PNG, at most 2 MB): base64, sealed at rest by the API. */
export function readDocument(file: File): Promise<{ documentBase64: string; documentType: string }> {
  return new Promise((resolve, reject) => {
    if (!['application/pdf', 'image/jpeg', 'image/png'].includes(file.type)) return reject(new Error('Dokumentet må være PDF, JPEG eller PNG.'));
    if (file.size > 2 * 1024 * 1024) return reject(new Error('Dokumentet er større enn 2 MB.'));
    const fr = new FileReader();
    fr.onerror = () => reject(new Error('Filen kunne ikke leses.'));
    fr.onload = () => {
      const b = new Uint8Array(fr.result as ArrayBuffer); let bin = '';
      for (let i = 0; i < b.length; i += 0x8000) bin += String.fromCharCode.apply(null, Array.from(b.subarray(i, i + 0x8000)));
      resolve({ documentBase64: btoa(bin), documentType: file.type });
    };
    fr.readAsArrayBuffer(file);
  });
}
